'use client';
import React, { useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Flag, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  postId?: string;
  commentId?: string;
  onClose: () => void;
}

const reasons = [
  'Spam or misleading',
  'Harassment or bullying',
  'Inappropriate content',
  'Intellectual property violation',
  'Other',
];

export default function ReportModal({ postId, commentId, onClose }: Props) {
  const { user } = useAuth();
  const supabase = createClient();
  const [selectedReason, setSelectedReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!user || !selectedReason) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.from('reports').insert({
        reporter_id: user.id,
        report_type: postId ? 'post' : 'comment',
        post_id: postId || null,
        comment_id: commentId || null,
        reason: selectedReason,
      });
      if (error) throw error;
      toast.success('Report submitted. Our team will review it.');
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm fade-in"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl border border-cream-300 shadow-2xl w-full max-w-sm slide-up">
        <div className="flex items-center justify-between px-5 py-4 border-b border-cream-200">
          <div className="flex items-center gap-2">
            <Flag size={15} className="text-red-500" />
            <span className="text-sm font-semibold text-charcoal-900">Report {postId ? 'Post' : 'Comment'}</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-cream-200 text-charcoal-400 transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-charcoal-600">Why are you reporting this?</p>
          {reasons.map(reason => (
            <label key={reason} className="flex items-center gap-3 cursor-pointer group">
              <input type="radio" name="reason" value={reason} checked={selectedReason === reason}
                onChange={() => setSelectedReason(reason)}
                className="w-4 h-4 text-charcoal-900 border-cream-400 focus:ring-gold-400/40" />
              <span className="text-sm text-charcoal-700 group-hover:text-charcoal-900 transition-colors">{reason}</span>
            </label>
          ))}
          <button onClick={handleSubmit} disabled={!selectedReason || submitting}
            className="w-full flex items-center justify-center gap-2 py-3 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 disabled:opacity-50 active:scale-[0.98] transition-all mt-4">
            {submitting ? <Loader2 size={15} className="animate-spin" /> : <Flag size={15} />}
            {submitting ? 'Submitting…' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
}
